import React from 'react';

/**
 * Top-level error boundary. Previously a render-time exception in any child
 * component would bubble up and unmount the entire tree, leaving a bare
 * black `#050505` background — the mobile "black screen after login" bug.
 * This boundary catches it, shows a readable fallback, and offers a reload.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    // Soft-refresh just this boundary instead of a hard page reload
    // to avoid the mobile hydration race observed in production.
    try {
      const evt = new Event('auth-changed');
      window.dispatchEvent(evt);
    } catch (_) { /* noop */ }
  };

  handleSignOut = () => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } catch (_) { /* noop */ }
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-neutral-900/60 border border-white/10 rounded-2xl p-6 md:p-8">
          <div className="text-4xl mb-3">⚠️</div>
          <h1 className="text-xl md:text-2xl font-bold mb-2">
            Something went wrong.
          </h1>
          <p className="text-sm text-neutral-400 mb-6">
            We hit an error rendering this page. You can try again, or sign
            out and back in if the problem keeps happening.
          </p>
          {this.state.error?.message && (
            <pre className="text-xs bg-black/40 border border-white/10 rounded-md p-3 overflow-x-auto mb-6 text-neutral-400">
              {String(this.state.error.message).slice(0, 400)}
            </pre>
          )}
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={this.handleReset}
              className="flex-1 bg-[#00F0FF] text-black font-semibold py-2 px-4 rounded-lg hover:bg-[#00F0FF]/90"
              data-testid="error-boundary-retry"
            >
              Try again
            </button>
            <button
              onClick={this.handleSignOut}
              className="flex-1 bg-white/5 border border-white/10 text-white py-2 px-4 rounded-lg hover:bg-white/10"
              data-testid="error-boundary-signout"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
